import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  AIToolUiStyleHost,
  RevoltTextarea,
  RevoltPopup,
  RevoltIcon,
  RevoltModal,
  RevoltButton,
  RevoltInput,
} from "../../../components";
import { logger } from "../../../core";
import { linkUtils, envUtils } from "../../../core/utils";
import { TANDEM_LANES } from "../../../systems/core/instances/tandem/base/TandemBubbleUiSystem.js";
import { ModelsModal } from "../../workspace/components/ModelsModal";
import { LocalModelsModal } from "../../workspace/components/LocalModelsModal";
import { LocalFlashAttentionToggleButton } from "../../workspace/components/LocalFlashAttentionToggleButton";
import { LocalContextSizeControl } from "../../workspace/components/LocalContextSizeControl";
import { LocalContextAllocationControl } from "../../workspace/components/LocalContextAllocationControl";
import { LocalModelInfoModal } from "../../workspace/components/LocalModelInfoModal";
import { ImageGalleryModal } from "../../workspace/components/ImageGalleryModal";
import { FileViewerModal } from "../../workspace/components/FileViewerModal";
import "../../workspace/components/AIChatInterface.css";
import "../../workspace/components/ChatBubble.css";
import "../../workspace/components/TransferTab.css";
import "./TandemChatInterface.css"; // layout gutters + path line

/**
 * Desktop Tandem chat column — mirrors workspace AI chat model UX and bubble host,
 * without app-membership persistence (session-only selection via React state).
 *
 * @param {Object} props
 * @param {Object|null} props.user
 * @param {import("../managers/tandem-manager").TandemManager} props.tandemManager
 * @param {React.MutableRefObject<Record<string, unknown>|null>} props.tandemChatInterfaceRef
 * @param {string} [props.workingDirectoryPath]
 * @param {string} [props.workingProjectName] — label from saved Tandem project (optional)
 * @param {string} [props.workingProjectId] — Revolt Desktop `tandem-projects.json` project id
 * @param {() => void} [props.onBackToDirectory]
 */
export function TandemChatInterface({
  user,
  tandemManager: manager,
  tandemChatInterfaceRef,
  workingDirectoryPath = "",
  workingProjectName = "",
  workingProjectId = "",
  onBackToDirectory,
}) {
  const bubbleUiSystemRef = useRef(null);
  const bubbleUiHostRef = useRef(null);
  const attachmentButtonRef = useRef(null);
  const modelButtonRef = useRef(null);
  const fileInputRef = useRef(null);
  const localLoadProgressCleanupRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [isDragOverTextarea, setIsDragOverTextarea] = useState(false);
  const [showModelsModal, setShowModelsModal] = useState(false);
  const [showAddModelChoiceModal, setShowAddModelChoiceModal] = useState(false);
  const [showLocalModelsModal, setShowLocalModelsModal] = useState(false);
  const [showLocalModelInfoModal, setShowLocalModelInfoModal] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return envUtils.device.getRecommendedUIMode() === "mobile";
  });
  const [showLocalKnobsPopup, setShowLocalKnobsPopup] = useState(false);
  const [localKnobsPopupPosition, setLocalKnobsPopupPosition] = useState({ top: 50, left: 50 });
  const [showImageGalleryModal, setShowImageGalleryModal] = useState(false);
  const [showFileViewerModal, setShowFileViewerModal] = useState(false);
  const [imageGalleryIndex, setImageGalleryIndex] = useState(0);
  const [fileViewerIndex, setFileViewerIndex] = useState(0);
  const [currentImageUrls, setCurrentImageUrls] = useState([]);
  const [currentFileSegments, setCurrentFileSegments] = useState([]);
  const [imageGalleryKey, setImageGalleryKey] = useState("image-gallery-initial");
  const [fileViewerKey, setFileViewerKey] = useState("file-viewer-initial");

  const [selectedModel, setSelectedModel] = useState(null);
  const [favoritedModels, setFavoritedModels] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [savedCloudModelId, setSavedCloudModelId] = useState(null);
  const [savedLocalModelId, setSavedLocalModelId] = useState(null);
  const [savedModelType, setSavedModelType] = useState("cloud");
  const [localModels, setLocalModels] = useState([]);
  const [localModelsRefreshTrigger, setLocalModelsRefreshTrigger] = useState(0);
  const [inferenceError, setInferenceError] = useState(null);

  const [localModelLoadState, setLocalModelLoadState] = useState("idle");
  const [localModelLoadProgress, setLocalModelLoadProgress] = useState(0);
  const [localModelStatusMessage, setLocalModelStatusMessage] = useState(null);
  const [, setLocalModelStatusType] = useState("info");
  const [localContextSizeMode, setLocalContextSizeMode] = useState("auto");
  const [localContextSizeMin, setLocalContextSizeMin] = useState("8096");
  const [localContextSizeMax, setLocalContextSizeMax] = useState("16384");
  const [localContextAllocationMode, setLocalContextAllocationMode] = useState("auto");
  const [localContextAllocationSize, setLocalContextAllocationSize] = useState("16384");
  const [localDefaultContextFlashAttention, setLocalDefaultContextFlashAttention] = useState(true);
  const [localContextMetrics, setLocalContextMetrics] = useState(null);

  const [uiActionLocked, setUiActionLocked] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationFollowEnabled, setGenerationFollowEnabled] = useState(true);

  const [showManageChatsModal, setShowManageChatsModal] = useState(false);
  const [manageChatSummaries, setManageChatSummaries] = useState([]);
  const [manageChatSearch, setManageChatSearch] = useState("");
  const [manageChatsBusy, setManageChatsBusy] = useState(false);
  const [renameChatId, setRenameChatId] = useState("");
  const [renameChatDraft, setRenameChatDraft] = useState("");

  const isMutationBlocked = isGenerating || uiActionLocked;
  const isUiBlocked = uiActionLocked;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(envUtils.device.getRecommendedUIMode() === "mobile");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const bubbleUiSystem = manager?.tandemBubbleUiSystem || null;
    bubbleUiSystemRef.current = bubbleUiSystem;
    if (!bubbleUiSystem) {
      return undefined;
    }
    bubbleUiSystem.mount(bubbleUiHostRef.current);
    return () => {
      bubbleUiSystem.unmount();
    };
  }, [manager]);

  useEffect(() => {
    return () => {
      attachments.forEach((att) => {
        if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
      });
    };
  }, [attachments]);

  useEffect(() => {
    const fetchAllModels = async () => {
      try {
        const response = await fetch("/api/openrouter/models", {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAllModels(data.models || []);
          }
        }
      } catch (error) {
        logger.error(
          { tags: "tandem-chat|error|fetch-all-models", color1: "red" },
          "Failed to fetch all models:",
          error
        );
      }
    };
    fetchAllModels();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.modelsAPI?.getAll) {
      setLocalModels([]);
      return;
    }
    let cancelled = false;

    function pickFirstValue(...values) {
      for (const v of values) {
        if (v === undefined || v === null) continue;
        if (typeof v === "string") {
          if (v.trim().length === 0) continue;
          return v;
        }
        return v;
      }
      return null;
    }

    function getOverviewFromMeta(ggufMeta) {
      if (!ggufMeta) return null;
      const general = ggufMeta?.metadata?.general || null;
      const baseModel0 = general?.base_model?.["0"] ?? general?.base_model?.[0] ?? null;
      const architecture = general?.architecture ?? null;
      const publisher = pickFirstValue(
        baseModel0?.organization,
        general?.organization,
        general?.publisher
      );
      const displayName = pickFirstValue(baseModel0?.name, general?.name);
      let contextLength = null;
      if (architecture === "llama" && ggufMeta?.metadata?.llama) {
        contextLength = ggufMeta.metadata.llama.context_length ?? null;
      } else if (architecture === "gpt-oss" && ggufMeta?.metadata?.["gpt-oss"]) {
        contextLength = ggufMeta.metadata["gpt-oss"].context_length ?? null;
      } else if (architecture === "qwen3" && ggufMeta?.metadata?.qwen3) {
        contextLength = ggufMeta.metadata.qwen3.context_length ?? null;
      } else if (architecture === "qwen35" && ggufMeta?.metadata?.qwen35) {
        contextLength = ggufMeta.metadata.qwen35.context_length ?? null;
      }
      return { publisher, displayName, architecture, contextLength };
    }

    const load = async () => {
      try {
        const models = await window.modelsAPI.getAll();
        if (cancelled) return;
        const list = models || [];
        const normalized = list.map((m) => {
          const cached = m?.metadata?.insightsOverview;
          const provider =
            cached?.publisher && String(cached.publisher).trim()
              ? cached.publisher.trim()
              : "—";
          return {
            id: m.id,
            name: m.displayName || m.name || m.id,
            provider,
            source: "local",
            isActive: m.isActive,
            path: m.path,
            metadata: m.metadata || {},
          };
        });

        if (window.llamaCppAPI?.getInsights && window.modelsAPI?.setInsightsCache && list.length > 0) {
          const withProviders = await Promise.all(
            list.map(async (m, i) => {
              if (cancelled) return normalized[i];
              const item = normalized[i];
              if (m?.metadata?.insightsOverview) return item;
              if (!m.path) return item;
              try {
                const result = await window.llamaCppAPI.getInsights(m.path);
                const ggufMeta = result?.success ? result.metadata : null;
                const overview = ggufMeta ? getOverviewFromMeta(ggufMeta) : null;
                const displayProvider =
                  overview?.publisher && String(overview.publisher).trim()
                    ? overview.publisher.trim()
                    : "—";
                if (overview) {
                  try {
                    await window.modelsAPI.setInsightsCache(m.id, overview);
                  } catch (_) {
                    /* ignore */
                  }
                }
                return { ...item, provider: displayProvider };
              } catch (_) {
                return item;
              }
            })
          );
          if (!cancelled) setLocalModels(withProviders);
        } else if (!cancelled) {
          setLocalModels(normalized);
        }
      } catch (err) {
        if (!cancelled) {
          logger.warn(
            { tags: "tandem-chat|local-models", color1: "yellow" },
            "Failed to load local models:",
            err
          );
          setLocalModels([]);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [localModelsRefreshTrigger]);

  const fetchFavoritedModels = useCallback(async () => {
    try {
      const response = await fetch("/api/account/models", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const favorited = Object.entries(data.models || {})
            .filter(([, modelData]) => modelData.favorite === true)
            .map(([modelId, modelData]) => {
              const fullModelData = allModels.find((m) => m.id === modelId);
              return {
                id: modelId,
                ...modelData,
                name: fullModelData?.name || modelId,
                provider: fullModelData?.provider || "Unknown",
              };
            });
          setFavoritedModels(favorited);
        }
      }
    } catch (error) {
      logger.error(
        { tags: "tandem-chat|error|fetch-favorited-models", color1: "red" },
        "Failed to fetch favorited models:",
        error
      );
    }
  }, [allModels]);

  useEffect(() => {
    if (allModels.length > 0) {
      fetchFavoritedModels();
    }
  }, [allModels, fetchFavoritedModels]);

  // Session-only auto-select (no app membership writes).
  useEffect(() => {
    if (selectedModel) return;
    if (favoritedModels.length === 0) return;

    const run = async () => {
      let modelToSelect = null;
      try {
        const activeModelResponse = await fetch("/api/account/settings/activeModel", {
          credentials: "include",
        });
        if (activeModelResponse.ok) {
          const activeModelData = await activeModelResponse.json();
          if (activeModelData.success && activeModelData.value) {
            modelToSelect =
              favoritedModels.find((m) => m.id === activeModelData.value) || null;
          }
        }
      } catch (error) {
        logger.log(
          { tags: "tandem-chat|auto-select-model|error", color1: "red" },
          "Failed to fetch activeModel:",
          error?.message
        );
      }

      if (!modelToSelect) {
        modelToSelect =
          favoritedModels.find((m) => m.id === "google/gemini-3-flash-preview") ||
          favoritedModels[0];
      }

      if (modelToSelect) {
        setSelectedModel({ ...modelToSelect, source: "cloud" });
        setSavedCloudModelId(modelToSelect.id);
        setSavedModelType("cloud");
      }
    };

    run();
  }, [favoritedModels, selectedModel]);

  useEffect(() => {
    const isDesktop = typeof window !== "undefined" && window.modelsAPI?.getAll;

    if (isDesktop && savedModelType === "local" && savedLocalModelId) {
      const found = localModels.find((m) => m.id === savedLocalModelId);
      const isActive = found && found.isActive !== false;
      const canUpgrade = selectedModel?.unavailable && found && isActive;
      if (found && isActive && (!selectedModel || canUpgrade)) {
        setSelectedModel({ ...found, source: "local" });
      } else if (!selectedModel || selectedModel?.unavailable) {
        const listLoaded = localModels.length >= 0;
        if (listLoaded && (!found || !isActive)) {
          setSelectedModel({
            id: savedLocalModelId,
            name: found?.name || savedLocalModelId,
            provider: found?.provider ?? "—",
            source: "local",
            unavailable: true,
          });
        }
      }
      return;
    }

    const idToResolve = savedCloudModelId;
    if (!idToResolve) return;
    if (selectedModel?.source === "cloud" && selectedModel?.id === idToResolve) return;

    if (favoritedModels.length > 0 || allModels.length > 0) {
      const fullModel =
        favoritedModels.find((m) => m.id === idToResolve) ||
        allModels.find((m) => m.id === idToResolve);
      if (fullModel) {
        setSelectedModel({ ...fullModel, source: "cloud" });
      }
    }
  }, [
    savedCloudModelId,
    savedLocalModelId,
    selectedModel,
    savedModelType,
    favoritedModels,
    allModels,
    localModels,
  ]);

  useEffect(() => {
    let cancelled = false;
    const api = typeof window !== "undefined" ? window.llamaCppAPI || null : null;
    const isDesktop = !!api?.isModelLoaded && !!api?.unloadModel;
    const modelIsLocal = selectedModel?.source === "local" && !!selectedModel?.path;

    if (!isDesktop || !modelIsLocal) {
      setLocalModelLoadState("idle");
      setLocalModelLoadProgress(0);
      setLocalModelStatusMessage(null);
      setLocalModelStatusType("info");
      setLocalContextMetrics(null);
      return () => {};
    }

    api
      .isModelLoaded()
      .then((res) => {
        if (cancelled) return;
        const info = res?.modelInfo || null;
        const isLoaded = res?.isLoaded === true;
        const matchesPath = !!info?.modelPath && info.modelPath === selectedModel.path;

        if (isLoaded && matchesPath) {
          setLocalModelLoadState("loaded");
          setLocalModelLoadProgress(0);
          setLocalModelStatusMessage("Model ready");
          setLocalModelStatusType("success");
          setLocalContextMetrics({
            runtime: info?.contextSize ?? null,
            train: info?.trainContextSize ?? null,
            gpuLayers: info?.gpuLayers ?? null,
            totalLayers: info?.totalLayers ?? null,
          });
        } else {
          setLocalModelLoadState("idle");
          setLocalModelLoadProgress(0);
          setLocalModelStatusMessage(null);
          setLocalModelStatusType("info");
          setLocalContextMetrics(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLocalModelLoadState("idle");
        setLocalModelLoadProgress(0);
        setLocalModelStatusMessage(null);
        setLocalModelStatusType("info");
        setLocalContextMetrics(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedModel]);

  useEffect(() => {
    if (selectedModel?.source !== "local") {
      setShowLocalKnobsPopup(false);
      setShowLocalModelInfoModal(false);
    }
  }, [selectedModel]);

  useEffect(() => {
    if (!selectedModel || selectedModel?.source !== "local") return;
    const devChatOptions = selectedModel?.metadata?.devChatOptions || {};
    setLocalDefaultContextFlashAttention(devChatOptions.defaultContextFlashAttention ?? true);
    setLocalContextSizeMode(devChatOptions.contextSizeMode ?? "auto");
    setLocalContextSizeMin(String(devChatOptions.contextSizeMin ?? "8096"));
    setLocalContextSizeMax(String(devChatOptions.contextSizeMax ?? "16384"));
    setLocalContextAllocationMode(devChatOptions.contextAllocationMode ?? "auto");
    setLocalContextAllocationSize(String(devChatOptions.contextAllocationSize ?? "16384"));
  }, [selectedModel]);

  const subscribeLocalLoadProgress = useCallback((callback) => {
    const api = typeof window !== "undefined" ? window.llamaCppAPI || null : null;
    if (!api?.onLoadProgress) return () => {};
    return api.onLoadProgress((payload) => {
      const p = typeof payload === "number" ? payload : payload?.progress ?? 0;
      callback(p);
    });
  }, []);

  const formatMetricSlashPair = (left, right) => {
    const l =
      left != null && left !== "" && !Number.isNaN(Number(left))
        ? Number(left).toLocaleString()
        : null;
    const r =
      right != null && right !== "" && !Number.isNaN(Number(right))
        ? Number(right).toLocaleString()
        : null;
    if (l != null && r != null) return `${l} / ${r}`;
    if (l != null) return l;
    if (r != null) return r;
    return "—";
  };

  const persistLocalModelOptionsPartial = useCallback(
    async (partial = {}) => {
      if (!selectedModel || selectedModel?.source !== "local") return;
      if (!selectedModel?.id) return;
      if (typeof window === "undefined") return;
      if (!window.modelsAPI?.setDevChatOptions) return;

      const cleaned = {};
      const keys = [
        "defaultContextFlashAttention",
        "contextSizeMode",
        "contextSizeMin",
        "contextSizeMax",
        "contextAllocationMode",
        "contextAllocationSize",
      ];
      for (const k of keys) {
        if (!Object.prototype.hasOwnProperty.call(partial, k)) continue;
        const v = partial[k];
        if (v === undefined || v === null) continue;
        cleaned[k] = typeof v === "string" ? v : v;
      }
      if (Object.keys(cleaned).length === 0) return;
      try {
        await window.modelsAPI.setDevChatOptions(selectedModel.id, cleaned);
      } catch (err) {
        logger.error(
          { tags: "tandem-chat|local-model-options|persist-error", color1: "red" },
          "Failed to persist local model options",
          err
        );
      }
    },
    [selectedModel]
  );

  const handleLocalLoad = useCallback(async () => {
    if (!selectedModel || selectedModel?.source !== "local" || !selectedModel?.path) return;
    if (localModelLoadState === "loading" || localModelLoadState === "unloading") return;
    const api = typeof window !== "undefined" ? window.llamaCppAPI || null : null;
    if (!api?.loadModel || !api?.isModelLoaded) return;

    if (localLoadProgressCleanupRef.current) {
      try {
        localLoadProgressCleanupRef.current();
      } catch (_) {}
      localLoadProgressCleanupRef.current = null;
    }

    setLocalModelLoadState("loading");
    setLocalModelLoadProgress(0);
    setLocalModelStatusMessage(null);
    setLocalModelStatusType("info");
    setLocalContextMetrics(null);

    const cleanup = subscribeLocalLoadProgress((p) => {
      const percent = p <= 1 ? Math.round(p * 100) : Math.round(p);
      setLocalModelLoadProgress(Math.min(100, Math.max(0, percent)));
    });
    localLoadProgressCleanupRef.current = cleanup;

    try {
      const contextSize =
        localContextSizeMode === "auto"
          ? "auto"
          : {
              min: Math.max(512, parseInt(localContextSizeMin, 10) || 8096),
              max: Math.min(128000, parseInt(localContextSizeMax, 10) || 16384),
            };
      const contextAllocationSize =
        localContextAllocationMode === "auto"
          ? undefined
          : parseInt(localContextAllocationSize, 10) || undefined;
      await api.loadModel(selectedModel.path, {
        contextSize,
        contextAllocationSize,
        defaultContextFlashAttention: localDefaultContextFlashAttention,
      });

      const loaded = await api.isModelLoaded();
      const info = loaded?.modelInfo || null;
      const matchesPath = !!info?.modelPath && info.modelPath === selectedModel.path;

      if (loaded?.isLoaded === true && matchesPath) {
        setLocalModelLoadState("loaded");
        setLocalModelLoadProgress(100);
        setLocalModelStatusMessage("Model ready");
        setLocalModelStatusType("success");
        setLocalContextMetrics({
          runtime: info?.contextSize ?? null,
          train: info?.trainContextSize ?? null,
          gpuLayers: info?.gpuLayers ?? null,
          totalLayers: info?.totalLayers ?? null,
        });
      } else {
        setLocalModelLoadState("error");
        setLocalModelLoadProgress(0);
        setLocalModelStatusMessage(loaded?.error || "Load finished but model not detected");
        setLocalModelStatusType("error");
        setLocalContextMetrics(null);
      }
    } catch (err) {
      setLocalModelLoadState("error");
      setLocalModelLoadProgress(0);
      setLocalModelStatusMessage(err?.message || "Load failed");
      setLocalModelStatusType("error");
      setLocalContextMetrics(null);
    } finally {
      if (localLoadProgressCleanupRef.current) {
        try {
          localLoadProgressCleanupRef.current();
        } catch (_) {}
        localLoadProgressCleanupRef.current = null;
      }
      setTimeout(() => setLocalModelLoadProgress(0), 350);
    }
  }, [
    selectedModel,
    localModelLoadState,
    subscribeLocalLoadProgress,
    localContextSizeMode,
    localContextSizeMin,
    localContextSizeMax,
    localContextAllocationMode,
    localContextAllocationSize,
    localDefaultContextFlashAttention,
  ]);

  const handleLocalUnload = useCallback(async () => {
    const api = typeof window !== "undefined" ? window.llamaCppAPI || null : null;
    if (!api?.unloadModel) return;
    if (localModelLoadState !== "loaded") return;
    if (!selectedModel || selectedModel?.source !== "local") return;

    setLocalModelLoadState("unloading");
    setLocalModelLoadProgress(0);
    setLocalModelStatusMessage(null);
    setLocalModelStatusType("info");
    setLocalContextMetrics(null);

    try {
      await api.unloadModel();
      setLocalModelLoadState("idle");
      setLocalModelLoadProgress(0);
      setLocalModelStatusMessage("Model unloaded");
      setLocalModelStatusType("success");
      setTimeout(() => setLocalModelStatusMessage(null), 900);
    } catch (err) {
      setLocalModelLoadState("error");
      setLocalModelStatusMessage(err?.message || "Unload failed");
      setLocalModelStatusType("error");
    }
  }, [selectedModel, localModelLoadState]);

  const handleImageClick = useCallback((bubbleId, imageIndex, allImages) => {
    void bubbleId;
    setCurrentImageUrls(allImages);
    setImageGalleryIndex(imageIndex);
    setImageGalleryKey(`image-gallery-${Date.now()}`);
    setShowImageGalleryModal(true);
  }, []);

  const handleFileClick = useCallback((bubbleId, fileIndex, allFiles) => {
    void bubbleId;
    setCurrentFileSegments(allFiles);
    setFileViewerIndex(fileIndex);
    setFileViewerKey(`file-viewer-${Date.now()}`);
    setShowFileViewerModal(true);
  }, []);

  useEffect(() => {
    const bubbleUiSystem = manager?.tandemBubbleUiSystem || null;
    bubbleUiSystemRef.current = bubbleUiSystem;
    if (!bubbleUiSystem) {
      return undefined;
    }
    bubbleUiSystem.setEventHandlers({
      onImageClick: handleImageClick,
      onFileClick: handleFileClick,
      onOpenUrl: (url) => window.open(url, "_blank"),
      onBubbleClickAction: () => {},
    });
    return () => {
      bubbleUiSystem.setEventHandlers({});
    };
  }, [manager, handleFileClick, handleImageClick]);

  useEffect(() => {
    bubbleUiSystemRef.current?.setIsModelGenerating?.(isGenerating);
  }, [isGenerating]);

  useEffect(() => {
    bubbleUiSystemRef.current?.setGenerationFollowEnabled?.(generationFollowEnabled);
  }, [generationFollowEnabled]);

  const setInferenceErrorDisplay = useCallback((error) => {
    setInferenceError(error);
  }, []);

  useEffect(() => {
    if (!tandemChatInterfaceRef) return;
    if (!tandemChatInterfaceRef.current) {
      tandemChatInterfaceRef.current = {};
    }
    Object.assign(tandemChatInterfaceRef.current, {
      getBubbles: () => bubbleUiSystemRef.current?.getBubbles?.() || [],
      streamingManager:
        bubbleUiSystemRef.current?.streamingManager || { elements: new Map() },
      setInputValue,
      setAttachments,
      setIsGenerating,
      setQueuedMessages: () => {},
      setQueueActionLocked: () => {},
      setPendingQueuedSendId: () => {},
      setUiActionLocked,
      setLocalModelLoadState,
      setLocalModelLoadProgress,
      setLocalModelStatusMessage,
      setLocalModelStatusType,
      setLocalContextMetrics,
      attachments,
      isGenerating,
      selectedModel,
      getWorkspaceSendExtras: () => {
        const mode = selectedModel?.source === "local" ? "local" : "cloud";
        return {
          inferenceMode: mode,
          requestOptions:
            mode === "local"
              ? {
                  localModelLoadOptions: {
                    contextSizeMode: localContextSizeMode,
                    contextSizeMin: localContextSizeMin,
                    contextSizeMax: localContextSizeMax,
                    contextAllocationMode: localContextAllocationMode,
                    contextAllocationSize: localContextAllocationSize,
                    defaultContextFlashAttention: localDefaultContextFlashAttention,
                  },
                }
              : {},
        };
      },
    });
  }, [
    tandemChatInterfaceRef,
    setInputValue,
    setAttachments,
    setIsGenerating,
    setUiActionLocked,
    setLocalModelLoadState,
    setLocalModelLoadProgress,
    setLocalModelStatusMessage,
    setLocalModelStatusType,
    setLocalContextMetrics,
    attachments,
    isGenerating,
    selectedModel,
    localContextSizeMode,
    localContextSizeMin,
    localContextSizeMax,
    localContextAllocationMode,
    localContextAllocationSize,
    localDefaultContextFlashAttention,
  ]);

  useEffect(() => {
    if (tandemChatInterfaceRef?.current) {
      tandemChatInterfaceRef.current.setInferenceError = setInferenceErrorDisplay;
    }
  }, [setInferenceErrorDisplay, tandemChatInterfaceRef]);

  useEffect(() => {
    const bubbleUiSystem = manager?.tandemBubbleUiSystem || bubbleUiSystemRef.current;
    bubbleUiSystemRef.current = bubbleUiSystem;
    if (manager && tandemChatInterfaceRef?.current && bubbleUiSystem) {
      manager.registerUIReferences(tandemChatInterfaceRef.current, bubbleUiSystem);
    }
    const pid =
      typeof workingProjectId === "string" ? workingProjectId.trim() : "";
    if (
      manager &&
      pid &&
      bubbleUiSystem &&
      typeof manager.bootstrapTandemChatForProject === "function"
    ) {
      void manager.bootstrapTandemChatForProject(pid, bubbleUiSystem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, workingProjectId]);

  const refreshManageChatList = useCallback(async () => {
    if (!manager?.listTandemChatSummaries) return;
    setManageChatsBusy(true);
    try {
      const rows = await manager.listTandemChatSummaries();
      setManageChatSummaries(Array.isArray(rows) ? rows : []);
    } catch (e) {
      logger.error(
        { tags: "section|tandem|manage-chats", color1: "red" },
        "list chats:",
        e
      );
      setManageChatSummaries([]);
    } finally {
      setManageChatsBusy(false);
    }
  }, [manager]);

  useEffect(() => {
    if (showManageChatsModal) {
      void refreshManageChatList();
    }
  }, [showManageChatsModal, refreshManageChatList]);

  const filteredManageChats = useMemo(() => {
    const q = manageChatSearch.trim().toLowerCase();
    if (!q) return manageChatSummaries;
    return manageChatSummaries.filter((c) => {
      const name = (c.name || "").toLowerCase();
      return name.includes(q) || String(c.id || "").toLowerCase().includes(q);
    });
  }, [manageChatSummaries, manageChatSearch]);

  const handleLoadManagedChat = useCallback(
    async (chatId) => {
      if (!chatId || isMutationBlocked || !manager?.loadTandemChatSession) return;
      await manager.loadTandemChatSession(chatId);
      setShowManageChatsModal(false);
    },
    [isMutationBlocked, manager]
  );

  const handleDeleteManagedChat = useCallback(
    async (chatId) => {
      if (!chatId || !manager?.deleteTandemChatById) return;
      const active = manager.activeTandemChatSessionId;
      const ok = await manager.deleteTandemChatById(chatId);
      if (ok) {
        await refreshManageChatList();
      }
      if (ok && chatId === active && manager.startFreshTandemChatSession) {
        manager.startFreshTandemChatSession(bubbleUiSystemRef.current);
      }
    },
    [manager, refreshManageChatList]
  );

  const commitRenameManagedChat = useCallback(async () => {
    const id = renameChatId;
    const name = renameChatDraft.trim();
    if (!id || !name || !manager?.renameTandemChatById) return;
    const ok = await manager.renameTandemChatById(id, name);
    if (ok) {
      setRenameChatId("");
      setRenameChatDraft("");
      await refreshManageChatList();
    }
  }, [renameChatId, renameChatDraft, manager, refreshManageChatList]);

  const handleSend = async () => {
    if (uiActionLocked) return;
    const trimmedInput = inputValue.trim();
    setInferenceError(null);
    if (!selectedModel) return;
    if (!trimmedInput && attachments.length === 0) return;

    const userId = user ? user.id : null;
    setInputValue("");
    attachments.forEach((att) => {
      if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
    });
    const attachmentsToSend = [...attachments];
    setAttachments([]);

    try {
      if (!selectedModel?.id) {
        throw new Error("No model selected");
      }
      const inferenceMode = selectedModel?.source === "local" ? "local" : "cloud";
      await manager.sendMessage({
        userMessage: trimmedInput,
        attachments: attachmentsToSend,
        modelId: selectedModel.id,
        userId,
        aiChatInterface: tandemChatInterfaceRef.current,
        bubbleUiSystem: bubbleUiSystemRef.current,
        inferenceMode,
        tandemHandoffPolicy: "full",
        requestOptions:
          inferenceMode === "local"
            ? {
                localModelLoadOptions: {
                  contextSizeMode: localContextSizeMode,
                  contextSizeMin: localContextSizeMin,
                  contextSizeMax: localContextSizeMax,
                  contextAllocationMode: localContextAllocationMode,
                  contextAllocationSize: localContextAllocationSize,
                  defaultContextFlashAttention: localDefaultContextFlashAttention,
                },
              }
            : {},
      });
    } catch (error) {
      setIsGenerating(false);
      const normalizedError =
        error instanceof Error ? error : new Error(error?.message || String(error));
      setInferenceError(normalizedError);
      logger.error(
        { tags: "tandem-chat|handle-send|error", color1: "red" },
        "Failed to send message:",
        error
      );
    }
  };

  const handleResumeAfterError = useCallback(async () => {
    if (uiActionLocked || isMutationBlocked || !selectedModel || !inferenceError) {
      return;
    }
    const errorText = (inferenceError.message || "Unknown error").trim();
    const resumeMessage =
      `The previous response was interrupted by an error: "${errorText}". ` +
      `Please continue from where you left off as if this error did not occur.`;

    setInferenceError(null);
    const userId = user ? user.id : null;
    try {
      const inferenceMode = selectedModel?.source === "local" ? "local" : "cloud";
      await manager.sendMessage({
        userMessage: resumeMessage,
        attachments: [],
        modelId: selectedModel.id,
        userId,
        aiChatInterface: tandemChatInterfaceRef.current,
        bubbleUiSystem: bubbleUiSystemRef.current,
        inferenceMode,
        tandemHandoffPolicy: "resume",
        lane: TANDEM_LANES.INSTRUCTOR,
        requestOptions:
          inferenceMode === "local"
            ? {
                localModelLoadOptions: {
                  contextSizeMode: localContextSizeMode,
                  contextSizeMin: localContextSizeMin,
                  contextSizeMax: localContextSizeMax,
                  contextAllocationMode: localContextAllocationMode,
                  contextAllocationSize: localContextAllocationSize,
                  defaultContextFlashAttention: localDefaultContextFlashAttention,
                },
              }
            : {},
      });
    } catch (error) {
      setIsGenerating(false);
      const normalizedError =
        error instanceof Error ? error : new Error(error?.message || String(error));
      setInferenceError(normalizedError);
    }
  }, [
    uiActionLocked,
    isMutationBlocked,
    selectedModel,
    inferenceError,
    user,
    manager,
    tandemChatInterfaceRef,
    localContextSizeMode,
    localContextSizeMin,
    localContextSizeMax,
    localContextAllocationMode,
    localContextAllocationSize,
    localDefaultContextFlashAttention,
  ]);

  const handleCancelGeneration = () => {
    if (manager) {
      logger.log(
        { tags: "tandem-chat|cancellation", color1: "yellow" },
        "Cancelling tandem generation…"
      );
      manager.cancelGeneration();
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAttachmentMenu &&
        attachmentButtonRef.current &&
        typeof attachmentButtonRef.current.contains === "function" &&
        !attachmentButtonRef.current.contains(event.target) &&
        !event.target.closest(".ai-chat-attachment-menu")
      ) {
        setShowAttachmentMenu(false);
      }
      if (
        showModelMenu &&
        modelButtonRef.current &&
        typeof modelButtonRef.current.contains === "function" &&
        !modelButtonRef.current.contains(event.target) &&
        !event.target.closest(".ai-chat-model-menu")
      ) {
        setShowModelMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAttachmentMenu, showModelMenu]);

  const handleAttachmentClick = () => {
    setShowAttachmentMenu(!showAttachmentMenu);
  };

  const handleUploadFile = () => {
    if (fileInputRef.current && typeof fileInputRef.current.click === "function") {
      fileInputRef.current.click();
    }
    setShowAttachmentMenu(false);
  };

  const handleFileSelected = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const newAttachments = files.map((file) => {
        const isImage = file.type.startsWith("image/");
        return {
          id: crypto.randomUUID(),
          file,
          name: file.name,
          type: isImage ? "image" : "file",
          previewUrl: isImage ? URL.createObjectURL(file) : null,
        };
      });
      setAttachments((prev) => [...prev, ...newAttachments]);
    }
    if (fileInputRef.current && typeof fileInputRef.current.value !== "undefined") {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (id) => {
    setAttachments((prev) => {
      const attToRemove = prev.find((att) => att.id === id);
      if (attToRemove?.previewUrl) URL.revokeObjectURL(attToRemove.previewUrl);
      return prev.filter((att) => att.id !== id);
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    const newAttachments = files.map((file) => {
      const isImage = file.type.startsWith("image/");
      return {
        id: crypto.randomUUID(),
        file,
        name: file.name,
        type: isImage ? "image" : "file",
        previewUrl: isImage ? URL.createObjectURL(file) : null,
      };
    });
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  return (
    <div className="tandem-chat-layout">
      <div className="tandem-chat-layout__column">
        <div className="ai-chat-interface tandem-chat-interface--embed">
          <div className="ai-chat-header tandem-chat-interface__header">
            <div className="ai-chat-header-title">
              <h3>Tandem chat</h3>
              {workingProjectName ? (
                <span className="tandem-chat-interface__project-name" title={workingProjectName}>
                  {workingProjectName}
                </span>
              ) : null}
              {workingDirectoryPath ? (
                <span className="tandem-chat-interface__path" title={workingDirectoryPath}>
                  {workingDirectoryPath}
                </span>
              ) : null}
              {isGenerating ? (
                <div className="ai-chat-generating-indicator">
                  <i className="fa-solid fa-spinner fa-spin" />
                  <span>Generating…</span>
                </div>
              ) : null}
            </div>
            <div className="ai-chat-header-actions">
              {typeof onBackToDirectory === "function" ? (
                <RevoltButton
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (isMutationBlocked) return;
                    onBackToDirectory();
                  }}
                  disabled={isMutationBlocked}
                  title="Change working folder"
                >
                  Folder
                </RevoltButton>
              ) : null}
              {workingProjectId?.trim() ? (
                <button
                  type="button"
                  className="ai-chat-manage-button"
                  onClick={() => {
                    if (isMutationBlocked) return;
                    setManageChatSearch("");
                    setRenameChatId("");
                    setRenameChatDraft("");
                    setShowManageChatsModal(true);
                  }}
                  disabled={isMutationBlocked}
                  title="Manage chats"
                  aria-label="Manage chats"
                >
                  <RevoltIcon name="message-square" />
                </button>
              ) : null}
              <button
                type="button"
                className="ai-chat-new-button"
                onClick={() => {
                  if (isMutationBlocked) return;
                  manager?.handleNewChat?.({
                    aiChatInterface: tandemChatInterfaceRef?.current,
                    bubbleUiSystem: bubbleUiSystemRef.current,
                  });
                }}
                disabled={isMutationBlocked}
                title="New chat"
                aria-label="New chat"
              >
                <RevoltIcon name="plus" />
              </button>
            </div>
          </div>

          <div className="ai-chat-content-container">
            <AIToolUiStyleHost aiToolSystem={manager?.tandemAIToolSystem || null} />
            <div ref={bubbleUiHostRef} className="ai-chat-bubble-host" />
          </div>

          <div className="ai-chat-footer">
            {attachments.length > 0 ? (
              <div className="ai-chat-attachments-row">
                {attachments.map((att) => (
                  <div key={att.id} className="ai-chat-attachment-item">
                    {att.type === "image" ? (
                      <img
                        src={att.previewUrl}
                        alt={att.name}
                        className="ai-chat-attachment-preview"
                      />
                    ) : (
                      <div className="ai-chat-attachment-file-icon">
                        <RevoltIcon name="file" />
                      </div>
                    )}
                    <button
                      type="button"
                      className="ai-chat-attachment-remove"
                      onClick={() => handleRemoveAttachment(att.id)}
                      title="Remove"
                    >
                      <RevoltIcon name="x" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            {inferenceError ? (
              <div className="ai-chat-error-banner">
                <div className="ai-chat-error-content">
                  <RevoltIcon name="alert-triangle" size={16} />
                  <span
                    className="ai-chat-error-message"
                    dangerouslySetInnerHTML={{
                      __html: linkUtils.processErrorMessageLinks(inferenceError.message),
                    }}
                  />
                  <button
                    type="button"
                    className="ai-chat-error-resume"
                    onClick={handleResumeAfterError}
                    disabled={isMutationBlocked || !selectedModel}
                    title="Send a follow-up asking the model to continue"
                  >
                    Send resume message
                  </button>
                  <button
                    type="button"
                    className="ai-chat-error-dismiss"
                    onClick={() => setInferenceError(null)}
                    title="Dismiss error"
                  >
                    <RevoltIcon name="x" size={14} />
                  </button>
                </div>
              </div>
            ) : null}

            <div className="ai-chat-footer-grid">
              <div className="ai-chat-footer-left">
                <div className="ai-chat-input-row ai-chat-input-row--textarea-only">
                  <RevoltTextarea
                    className={`ai-chat-input ${isDragOverTextarea ? "drag-over" : ""}`}
                    placeholder={
                      selectedModel ? "Ask anything…" : "Select a model to start chatting…"
                    }
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onSubmit={handleSend}
                    rows={3}
                    minRows={3}
                    maxRows={5}
                    autoResize
                    disabled={!selectedModel || isUiBlocked}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setIsDragOverTextarea(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragOverTextarea(false);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!isDragOverTextarea) setIsDragOverTextarea(true);
                    }}
                    onDrop={(e) => {
                      setIsDragOverTextarea(false);
                      handleFileDrop(e);
                    }}
                  />
                </div>

                <div className="ai-chat-model-row">
                  <div className="ai-chat-model-wrapper" ref={modelButtonRef}>
                    <button
                      type="button"
                      className="ai-chat-model-button"
                      onClick={() => setShowModelMenu(!showModelMenu)}
                      title="Select model"
                      aria-label="Select model"
                    >
                      <span>
                        {selectedModel
                          ? selectedModel.name || selectedModel.id
                          : "Select a model…"}
                      </span>
                      <RevoltIcon
                        name="chevron-down"
                        className={`model-chevron ${showModelMenu ? "open" : ""}`}
                      />
                    </button>
                    {showModelMenu &&
                      (() => {
                        const saveSelectedModel = async (modelId, type) => {
                          setSavedModelType(type);
                          if (type === "cloud") {
                            setSavedCloudModelId(modelId);
                          } else {
                            setSavedLocalModelId(modelId);
                          }
                        };

                        const isDesktopEnv =
                          typeof window !== "undefined" && window.modelsAPI?.getAll;
                        const activeLocalModels = (localModels || []).filter(
                          (m) => m.isActive !== false
                        );
                        const empty =
                          !selectedModel &&
                          (isDesktopEnv
                            ? activeLocalModels.length === 0 && favoritedModels.length === 0
                            : favoritedModels.length === 0);

                        return (
                          <div className="ai-chat-model-menu">
                            <div className="ai-chat-model-menu-header">
                              <h4>Models</h4>
                              <button
                                type="button"
                                className="ai-chat-model-menu-header-add"
                                onClick={() => {
                                  setShowModelMenu(false);
                                  if (isDesktopEnv) {
                                    setShowAddModelChoiceModal(true);
                                  } else {
                                    setShowModelsModal(true);
                                  }
                                }}
                                title="Add models"
                                aria-label="Add models"
                              >
                                <RevoltIcon name="plus" />
                              </button>
                            </div>
                            <div className="ai-chat-model-list">
                              {empty ? (
                                <div className="ai-chat-no-models">
                                  <p>No favorited models found.</p>
                                  <p>Use + to add cloud favorites or enable local models.</p>
                                </div>
                              ) : (
                                <>
                                  {isDesktopEnv ? (
                                    <>
                                      {activeLocalModels.map((model) => (
                                        <div
                                          key={model.id}
                                          role="button"
                                          tabIndex={0}
                                          className={`ai-chat-model-option ${
                                            selectedModel?.id === model.id &&
                                            selectedModel?.source === "local"
                                              ? "selected"
                                              : ""
                                          }`}
                                          onClick={async () => {
                                            setSelectedModel({ ...model, source: "local" });
                                            setShowModelMenu(false);
                                            await saveSelectedModel(model.id, "local");
                                          }}
                                          onKeyDown={(ev) => {
                                            if (ev.key === "Enter" || ev.key === " ") {
                                              ev.preventDefault();
                                              setSelectedModel({ ...model, source: "local" });
                                              setShowModelMenu(false);
                                              void saveSelectedModel(model.id, "local");
                                            }
                                          }}
                                        >
                                          <div className="model-radio">
                                            <RevoltIcon
                                              name={
                                                selectedModel?.id === model.id &&
                                                selectedModel?.source === "local"
                                                  ? "check-circle"
                                                  : "circle"
                                              }
                                            />
                                          </div>
                                          <div className="model-info">
                                            <div className="model-name">
                                              {model.name || model.id}
                                            </div>
                                            <div className="model-provider">
                                              {model.provider || "—"}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      <div className="ai-chat-model-menu-divider" aria-hidden="true" />
                                    </>
                                  ) : null}

                                  {selectedModel?.source !== "local" &&
                                    selectedModel &&
                                    !favoritedModels.find((m) => m.id === selectedModel.id) ? (
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      className="ai-chat-model-option selected not-favorited"
                                      onClick={() => setShowModelMenu(false)}
                                      onKeyDown={(ev) => {
                                        if (ev.key === "Enter" || ev.key === " ") {
                                          ev.preventDefault();
                                          setShowModelMenu(false);
                                        }
                                      }}
                                    >
                                      <div className="model-radio">
                                        <RevoltIcon name="check-circle" />
                                      </div>
                                      <div className="model-info">
                                        <div className="model-name">
                                          {selectedModel.name || selectedModel.id}
                                        </div>
                                        <div className="model-provider">
                                          {selectedModel.provider || "Unknown"}
                                        </div>
                                        <div className="model-status">
                                          Model is not favorited anymore
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}

                                  {favoritedModels.map((model) => (
                                    <div
                                      key={model.id}
                                      role="button"
                                      tabIndex={0}
                                      className={`ai-chat-model-option ${
                                        selectedModel?.id === model.id &&
                                        selectedModel?.source !== "local"
                                          ? "selected"
                                          : ""
                                      }`}
                                      onClick={async () => {
                                        setSelectedModel({ ...model, source: "cloud" });
                                        setShowModelMenu(false);
                                        await saveSelectedModel(model.id, "cloud");
                                      }}
                                      onKeyDown={(ev) => {
                                        if (ev.key === "Enter" || ev.key === " ") {
                                          ev.preventDefault();
                                          setSelectedModel({ ...model, source: "cloud" });
                                          setShowModelMenu(false);
                                          void saveSelectedModel(model.id, "cloud");
                                        }
                                      }}
                                    >
                                      <div className="model-radio">
                                        <RevoltIcon
                                          name={
                                            selectedModel?.id === model.id &&
                                            selectedModel?.source !== "local"
                                              ? "check-circle"
                                              : "circle"
                                          }
                                        />
                                      </div>
                                      <div className="model-info">
                                        <div className="model-name">{model.name || model.id}</div>
                                        <div className="model-provider">
                                          {model.provider || "Unknown"}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                  </div>

                  {typeof window !== "undefined" &&
                  window.modelsAPI?.getAll &&
                  selectedModel?.source === "local" ? (
                    <div className="ai-chat-model-row-actions">
                      <div className="ai-chat-local-load-toggle-wrap">
                        <button
                          type="button"
                          className="ai-chat-local-load-toggle-btn"
                          onClick={
                            localModelLoadState === "loaded" ? handleLocalUnload : handleLocalLoad
                          }
                          disabled={
                            localModelLoadState === "loading" ||
                            localModelLoadState === "unloading" ||
                            (localModelLoadState !== "loaded" && !selectedModel?.path)
                          }
                          title={localModelLoadState === "loaded" ? "Unload model" : "Load model"}
                          aria-label={
                            localModelLoadState === "loaded" ? "Unload model" : "Load model"
                          }
                        >
                          <RevoltIcon
                            key={localModelLoadState === "loaded" ? "unload" : "load"}
                            name={localModelLoadState === "loaded" ? "square" : "lucide:play"}
                            size={16}
                          />
                        </button>
                      </div>
                      <button
                        type="button"
                        className="ai-chat-local-more-toggle-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (localModelLoadState === "loading" || localModelLoadState === "unloading") {
                            return;
                          }
                          const popupWidth = 380;
                          const popupHeight = 320;
                          if (isMobile) {
                            setShowLocalKnobsPopup(true);
                            return;
                          }
                          const rect = e.currentTarget.getBoundingClientRect();
                          const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
                          const left = clamp(rect.left, 12, window.innerWidth - popupWidth - 12);
                          const top = clamp(
                            rect.top - popupHeight - 8,
                            12,
                            window.innerHeight - popupHeight - 12
                          );
                          setLocalKnobsPopupPosition({ top, left });
                          setShowLocalKnobsPopup(true);
                        }}
                        disabled={localModelLoadState === "loading" || localModelLoadState === "unloading"}
                        title="Local model options"
                        aria-label="Local model options"
                      >
                        <RevoltIcon name="lucide:more-horizontal" size={16} />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="ai-chat-footer-right">
                <div className="ai-chat-button-group">
                  <div className="ai-chat-footer-right-top">
                    <button
                      type="button"
                      className={[
                        "ai-chat-attachment-button",
                        "ai-chat-follow-toggle",
                        generationFollowEnabled
                          ? "ai-chat-follow-toggle--locked"
                          : "ai-chat-follow-toggle--unlocked",
                      ].join(" ")}
                      onClick={() => {
                        if (isUiBlocked) return;
                        setGenerationFollowEnabled((prev) => !prev);
                      }}
                      disabled={isUiBlocked}
                      title={
                        generationFollowEnabled
                          ? "Turn auto scrolling off"
                          : "Turn auto scrolling on"
                      }
                      aria-label="Toggle auto scrolling while generating"
                    >
                      <RevoltIcon
                        key={generationFollowEnabled ? "lock" : "unlock"}
                        name={generationFollowEnabled ? "lock" : "unlock"}
                      />
                    </button>
                  </div>
                  <div className="ai-chat-footer-right-bottom">
                    <div className="ai-chat-attachment-wrapper" ref={attachmentButtonRef}>
                      <button
                        type="button"
                        className="ai-chat-attachment-button"
                        onClick={handleAttachmentClick}
                        disabled={isUiBlocked}
                        title="Attach file"
                        aria-label="Attach file"
                      >
                        <RevoltIcon name="paperclip" />
                      </button>
                      {showAttachmentMenu ? (
                        <div className="ai-chat-attachment-menu">
                          <button
                            type="button"
                            className="ai-chat-attachment-menu-item"
                            onClick={handleUploadFile}
                          >
                            <RevoltIcon name="upload" />
                            <span>Upload a file</span>
                          </button>
                        </div>
                      ) : null}
                    </div>

                    {isGenerating ? (
                      <button
                        key="cancel-button"
                        type="button"
                        className="ai-chat-send-button ai-chat-cancel-button"
                        onClick={handleCancelGeneration}
                        disabled={isUiBlocked}
                        title="Stop generation"
                        aria-label="Stop generation"
                      >
                        <RevoltIcon name="square" />
                      </button>
                    ) : (
                      <button
                        key="send-button"
                        type="button"
                        className="ai-chat-send-button"
                        onClick={handleSend}
                        disabled={
                          !selectedModel ||
                          selectedModel.unavailable ||
                          (!inputValue.trim() && attachments.length === 0) ||
                          isUiBlocked
                        }
                        title="Send"
                        aria-label="Send"
                      >
                        <RevoltIcon name="send" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {typeof window !== "undefined" &&
            window.modelsAPI?.getAll &&
            selectedModel?.source === "local" &&
            (localModelLoadState === "loading" ||
              localModelLoadState === "unloading" ||
              localModelStatusMessage ||
              localContextMetrics) ? (
              <div
                className={[
                  "ai-chat-local-status-card",
                  localModelLoadState === "loading" || localModelLoadProgress > 0
                    ? "ai-chat-local-status-card--loading"
                    : "",
                  localModelLoadState === "unloading" ? "ai-chat-local-status-card--unloading" : "",
                  localModelLoadState === "loaded" ? "ai-chat-local-status-card--ready" : "",
                ].join(" ")}
              >
                <div className="ai-chat-local-status-card__header">
                  <div className="ai-chat-local-status-card__title">
                    {localModelLoadState === "loading" || localModelLoadProgress > 0
                      ? "Loading for inference"
                      : localModelLoadState === "unloading"
                        ? "Unloading"
                        : localModelLoadState === "loaded"
                          ? "Inference ready"
                          : "Local model"}
                  </div>
                  {(localModelLoadState === "loading" || localModelLoadState === "unloading") && (
                    <div className="ai-chat-local-status-card__progress-label">
                      {localModelLoadProgress}%
                    </div>
                  )}
                  {localContextMetrics && localModelLoadState === "loaded" ? (
                    <div className="ai-chat-local-status-card__chips ai-chat-local-status-card__chips--header">
                      <div className="ai-chat-local-status-chip ai-chat-local-status-chip--header">
                        <div className="ai-chat-local-status-chip__label">Context</div>
                        <div className="ai-chat-local-status-chip__value">
                          {formatMetricSlashPair(
                            localContextMetrics.runtime,
                            localContextMetrics.train
                          )}
                        </div>
                      </div>
                      <div className="ai-chat-local-status-chip ai-chat-local-status-chip--header">
                        <div className="ai-chat-local-status-chip__label">GPU layers</div>
                        <div className="ai-chat-local-status-chip__value">
                          {formatMetricSlashPair(
                            localContextMetrics.gpuLayers,
                            localContextMetrics.totalLayers
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="ai-chat-local-status-info-btn"
                        onClick={() => setShowLocalModelInfoModal(true)}
                        title="About local model loading"
                        aria-label="About local model loading"
                      >
                        <RevoltIcon name="help-circle" size={14} />
                      </button>
                    </div>
                  ) : null}
                </div>
                {(localModelLoadState === "loading" || localModelLoadProgress > 0) && (
                  <div className="ai-chat-local-status-card__progress">
                    <div
                      className="ai-chat-local-status-card__progress-fill"
                      style={{
                        width: `${Math.min(100, Math.max(0, localModelLoadProgress))}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            ) : null}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
          </div>

          {showManageChatsModal ? (
            <RevoltModal
              isOpen
              onClose={() => {
                setShowManageChatsModal(false);
                setRenameChatId("");
                setRenameChatDraft("");
              }}
              title="Manage chats"
            >
              <div className="tandem-manage-chats">
                <RevoltInput
                  variant="outline"
                  className="tandem-manage-chats__search"
                  placeholder="Search by name…"
                  value={manageChatSearch}
                  onChange={(e) => setManageChatSearch(e.target.value)}
                  autoComplete="off"
                />
                {manageChatsBusy ? (
                  <p className="tandem-manage-chats__hint">Loading…</p>
                ) : null}
                <div className="tandem-manage-chats__list" role="list">
                  {filteredManageChats.length === 0 && !manageChatsBusy ? (
                    <p className="tandem-manage-chats__hint">No saved chats yet.</p>
                  ) : null}
                  {filteredManageChats.map((row) => {
                    const isActive = row.id === manager?.activeTandemChatSessionId;
                    const isRenaming = renameChatId === row.id;
                    return (
                      <div
                        key={row.id}
                        className={
                          isActive
                            ? "tandem-manage-chats__row tandem-manage-chats__row--active"
                            : "tandem-manage-chats__row"
                        }
                        role="listitem"
                      >
                        <div className="tandem-manage-chats__row-main">
                          {isRenaming ? (
                            <div className="tandem-manage-chats__rename-row">
                              <RevoltInput
                                variant="outline"
                                value={renameChatDraft}
                                onChange={(e) => setRenameChatDraft(e.target.value)}
                                autoComplete="off"
                              />
                              <RevoltButton
                                type="button"
                                variant="accent"
                                onClick={() => void commitRenameManagedChat()}
                              >
                                Save
                              </RevoltButton>
                              <RevoltButton
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setRenameChatId("");
                                  setRenameChatDraft("");
                                }}
                              >
                                Cancel
                              </RevoltButton>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="tandem-manage-chats__open"
                              onClick={() => void handleLoadManagedChat(row.id)}
                              disabled={isMutationBlocked}
                            >
                              <span className="tandem-manage-chats__name">
                                {row.name || "Tandem chat"}
                              </span>
                              {isActive ? (
                                <span className="tandem-manage-chats__badge">Current</span>
                              ) : null}
                            </button>
                          )}
                        </div>
                        {!isRenaming ? (
                          <div className="tandem-manage-chats__row-actions">
                            <RevoltButton
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setRenameChatId(row.id);
                                setRenameChatDraft(row.name || "Tandem chat");
                              }}
                              disabled={isMutationBlocked}
                              title="Rename"
                              aria-label={`Rename ${row.name || "chat"}`}
                            >
                              <i className="fa-solid fa-pen" aria-hidden />
                            </RevoltButton>
                            <RevoltButton
                              type="button"
                              variant="outline"
                              onClick={() => void handleDeleteManagedChat(row.id)}
                              disabled={isMutationBlocked}
                              title="Delete"
                              aria-label={`Delete ${row.name || "chat"}`}
                            >
                              <i className="fa-solid fa-trash" aria-hidden />
                            </RevoltButton>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </RevoltModal>
          ) : null}

          {showAddModelChoiceModal ? (
            <RevoltModal
              isOpen
              onClose={() => setShowAddModelChoiceModal(false)}
              title="Add Model"
              showCloseButton={false}
              containerClassName="add-model-choice-modal"
            >
              <div className="add-model-choice-content">
                <div className="transfer-grid">
                  <div className="transfer-row">
                    <button
                      type="button"
                      className="transfer-card"
                      onClick={() => {
                        setShowAddModelChoiceModal(false);
                        setShowModelsModal(true);
                      }}
                    >
                      <div className="transfer-card-title">Cloud Models</div>
                      <div className="transfer-card-rule" />
                      <div className="transfer-card-description">
                        Browse and favorite API models
                      </div>
                    </button>
                    <button
                      type="button"
                      className="transfer-card"
                      onClick={() => {
                        setShowAddModelChoiceModal(false);
                        setShowLocalModelsModal(true);
                      }}
                    >
                      <div className="transfer-card-title">Local Models</div>
                      <div className="transfer-card-rule" />
                      <div className="transfer-card-description">
                        Enable or disable GGUF models on this device
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </RevoltModal>
          ) : null}

          <ModelsModal
            isOpen={showModelsModal}
            onClose={() => {
              setShowModelsModal(false);
              fetchFavoritedModels();
            }}
            user={user}
          />
          <LocalModelsModal
            isOpen={showLocalModelsModal}
            onClose={() => {
              setShowLocalModelsModal(false);
              setLocalModelsRefreshTrigger((t) => t + 1);
            }}
          />
          <LocalModelInfoModal
            isOpen={showLocalModelInfoModal}
            onClose={() => setShowLocalModelInfoModal(false)}
            metrics={localContextMetrics}
            flashAttentionEnabled={localDefaultContextFlashAttention}
            contextSizeMode={localContextSizeMode}
            contextSizeMin={localContextSizeMin}
            contextSizeMax={localContextSizeMax}
            allocationMode={localContextAllocationMode}
            allocationSize={localContextAllocationSize}
          />

          <RevoltPopup
            isOpen={showLocalKnobsPopup}
            onClose={() => setShowLocalKnobsPopup(false)}
            {...(isMobile
              ? {
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }
              : {
                  top: localKnobsPopupPosition.top,
                  left: localKnobsPopupPosition.left,
                })}
            width={isMobile ? "90vw" : "350px"}
            height={isMobile ? "70vh" : "360px"}
            maxWidth={isMobile ? "420px" : undefined}
            maxHeight={isMobile ? "520px" : undefined}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                color: "#ccc",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  flexShrink: 0,
                }}
              >
                <h3 style={{ margin: 0, color: "#fff", fontSize: "1.1rem" }}>
                  Local Model Options
                </h3>
                <button
                  type="button"
                  onClick={() => setShowLocalKnobsPopup(false)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid rgba(139, 0, 0, 0.3)",
                    background: "rgba(139, 0, 0, 0.12)",
                    color: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                  aria-label="Close"
                  title="Close"
                >
                  <RevoltIcon name="x" size={14} />
                </button>
              </div>
              <div
                style={{
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  overflowY: "auto",
                }}
              >
                <LocalFlashAttentionToggleButton
                  enabled={localDefaultContextFlashAttention}
                  isBusy={localModelLoadState === "loading" || localModelLoadState === "unloading"}
                  onToggle={() => {
                    setLocalDefaultContextFlashAttention((prev) => {
                      const next = !prev;
                      void persistLocalModelOptionsPartial({
                        defaultContextFlashAttention: next,
                      });
                      return next;
                    });
                  }}
                />
                <LocalContextSizeControl
                  mode={localContextSizeMode}
                  min={localContextSizeMin}
                  max={localContextSizeMax}
                  isBusy={localModelLoadState === "loading" || localModelLoadState === "unloading"}
                  onModeChange={(nextMode) => {
                    setLocalContextSizeMode(nextMode);
                    void persistLocalModelOptionsPartial({ contextSizeMode: nextMode });
                  }}
                  onMinChange={setLocalContextSizeMin}
                  onMaxChange={setLocalContextSizeMax}
                  onMinBlur={(value) => {
                    if (localContextSizeMode !== "manual") return;
                    const v = String(value ?? "").trim();
                    if (!v) return;
                    void persistLocalModelOptionsPartial({ contextSizeMin: v });
                  }}
                  onMaxBlur={(value) => {
                    if (localContextSizeMode !== "manual") return;
                    const v = String(value ?? "").trim();
                    if (!v) return;
                    void persistLocalModelOptionsPartial({ contextSizeMax: v });
                  }}
                />
                <LocalContextAllocationControl
                  mode={localContextAllocationMode}
                  size={localContextAllocationSize}
                  isBusy={localModelLoadState === "loading" || localModelLoadState === "unloading"}
                  onModeChange={(nextMode) => {
                    setLocalContextAllocationMode(nextMode);
                    void persistLocalModelOptionsPartial({
                      contextAllocationMode: nextMode,
                    });
                  }}
                  onSizeChange={setLocalContextAllocationSize}
                  onSizeBlur={(value) => {
                    if (localContextAllocationMode !== "manual") return;
                    const v = String(value ?? "").trim();
                    if (!v) return;
                    void persistLocalModelOptionsPartial({
                      contextAllocationSize: v,
                    });
                  }}
                />
              </div>
            </div>
          </RevoltPopup>

          <ImageGalleryModal
            key={imageGalleryKey}
            isOpen={showImageGalleryModal}
            onClose={() => setShowImageGalleryModal(false)}
            images={currentImageUrls}
            initialIndex={imageGalleryIndex}
          />
          <FileViewerModal
            key={fileViewerKey}
            isOpen={showFileViewerModal}
            onClose={() => setShowFileViewerModal(false)}
            fileSegments={currentFileSegments}
            initialIndex={fileViewerIndex}
          />
        </div>
      </div>
    </div>
  );
}
